import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { de } from "./dictionaries/de.js";
import { en } from "./dictionaries/en.js";
import { readStorage, writeStorage } from "../lib/storage.js";

export type Lang = "de" | "en";
export type TranslationKey = keyof typeof de;

const DICTIONARIES: Record<Lang, Record<TranslationKey, string>> = { de, en };

function detectInitialLang(): Lang {
  const stored = readStorage("lang");
  if (stored === "de" || stored === "en") return stored;
  // Spec §2: default by navigator.language, but never persisted until the
  // player actively switches.
  if (typeof navigator !== "undefined" && navigator.language?.startsWith("de")) return "de";
  return "en";
}

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => detectInitialLang());

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    // Only persisted on an active switch, per spec §2.
    writeStorage("lang", next);
  }, []);

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>) => {
      let text = DICTIONARIES[lang][key];
      if (vars) {
        for (const [name, value] of Object.entries(vars)) {
          text = text.replaceAll(`{${name}}`, String(value));
        }
      }
      return text;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
